import { Card } from "../../../card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

interface Metric {
  label: string;
  value: string | number;
  subtext?: string;
  subtextClass?: string; // Support for legacy metrics block
  color?: string; // Hex color override for the value/accent
  trend?: {
    direction: 'up' | 'down' | 'neutral';
    value?: string;
    color?: string;
  };
}

interface MetricsGridProps {
  data: {
    metrics: Metric[];
  };
}

// Orange accent colors that should trigger gradient styling
const ACCENT_ORANGE_COLORS = ['#ea580c', '#ff6b35', '#f97316'];

const isAccentOrange = (color?: string): boolean => {
  if (!color) return false;
  return ACCENT_ORANGE_COLORS.includes(color.toLowerCase());
};

export const MetricsGrid = ({ data }: MetricsGridProps) => {
  if (!data?.metrics || !Array.isArray(data.metrics)) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 my-8">
      {data.metrics.map((metric, idx) => {
        const useAccentGradient = isAccentOrange(metric.color);

        return (
          <Card
            key={idx}
            className={`p-4 sm:p-5 transition-all duration-200 ${
              useAccentGradient
                ? 'hover:border-orange-400 hover:shadow-[0_0_0_3px_#fff4f0,0_4px_20px_rgba(255,107,53,0.15)]'
                : ''
            }`}
          >
            <div className="text-xs uppercase tracking-wider text-neutral-500 font-medium">{metric.label}</div>
            <div
              className={`mt-1 text-2xl font-semibold ${
                useAccentGradient
                  ? 'bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500 bg-clip-text text-transparent drop-shadow-[0_1px_2px_rgba(255,107,53,0.2)]'
                  : 'text-neutral-900'
              }`}
              style={metric.color && !useAccentGradient ? { color: metric.color } : undefined}
            >
              {metric.value}
            </div>

            {(metric.subtext || metric.trend) && (
              <div className="flex items-center gap-2 mt-2 text-xs">
                {metric.trend && (
                  <div className={`flex items-center gap-1 font-medium ${
                    metric.trend.color ? `text-[${metric.trend.color}]` :
                    metric.trend.direction === 'up' ? 'text-emerald-600' :
                    metric.trend.direction === 'down' ? 'text-rose-600' :
                    'text-neutral-500'
                  }`}>
                    {metric.trend.direction === 'up' && <TrendingUp className="w-3 h-3" />}
                    {metric.trend.direction === 'down' && <TrendingDown className="w-3 h-3" />}
                    {metric.trend.direction === 'neutral' && <Minus className="w-3 h-3" />}
                    {metric.trend.value}
                  </div>
                )}
                {metric.subtext && (
                  <div className={`${metric.subtextClass || 'text-neutral-500'}`}>
                    {metric.subtext}
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};
